import { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import IcFaceFocusingBase from 'src/assets/images/icFaceFocusingFill.svg';
import IcHdBase from 'src/assets/images/icHDFill.svg';
import IcLanguageBase from 'src/assets/images/icLanguageFill.svg';
import IcMobileDesktopViewFillBase from 'src/assets/images/icMobileDesktopViewFill.svg';
import Button from 'src/components/Button';
import ModalLayoutBase, { ModalContent as ModalContentBase, ModalFooter as ModalFooterBase } from 'src/components/ModalLayout';
import { CreatorOnboardingStep, creatorOnboardingStepAtom } from 'src/stores/creator/atom';
import { ModalNavButtonType } from 'src/types/Modal';
import { hexToRgb } from 'src/utils/common';
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
  padding: 40px 24px 24px 24px;
  background: radial-gradient(
      65.62% 12.53% at 50% 0%,
      ${({
  theme
}) => `rgba(${hexToRgb(theme.color.primary1)}, 0.15)`} 0%,
      ${({
  theme
}) => `rgba(${hexToRgb(theme.color.primary1)}, 0)`} 100%
    ),
    ${({
  theme
}) => theme.color.bg1};
`;
const ModalContent = styled(ModalContentBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
`;
const WelcomeImage = styled.img`
  width: 100px;
  height: 100px;
  margin-bottom: 24px;
`;
const WelcomeHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;
const WelcomeTitle = styled.p`
  font-weight: 700;
  font-size: 24px;
  line-height: 30px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const WelcomeDescription = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const CreatorBenefitsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
const CreatorBenefitsBox = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  padding: 16px;
  gap: 16px;
  background-color: ${({
  theme
}) => `rgba(${hexToRgb(theme.color.surfaceDeep)}, 0.05)`};
  width: 100%;
`;
const BenefitsTitle = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.content9};
`;
const BenefitsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const BenefitItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const BenefitItemIconCss = ({
  theme
}: {
  theme: Theme;
}) => css`
  width: 20px;
  height: 20px;
  color: ${theme.color.contentDeep};
`;
const BenefitItemImage = styled.img`
  ${BenefitItemIconCss};
`;
const IcFaceFocusing = styled(IcFaceFocusingBase)`
  ${BenefitItemIconCss};
`;
const IcHd = styled(IcHdBase)`
  ${BenefitItemIconCss};
`;
const IcLanguage = styled(IcLanguageBase)`
  ${BenefitItemIconCss};
`;
const BenefitItemDescription = styled.span`
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  color: ${({
  theme
}) => `rgba(${hexToRgb(theme.color.contentDeep)}, 0.9)`};
`;
const ModalFooter = styled(ModalFooterBase)`
  padding: 16px 0 0 0;
`;
const FooterButton = styled(Button)`
  padding: 16px 24px;
`;
const IcMobileDesktopViewFill = styled(IcMobileDesktopViewFillBase)`
  width: 20px;
  height: 20px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const CreatorWelcomeModal: FC = () => {
  const setCreatorOnboardingStep = useSetAtom(creatorOnboardingStepAtom);
  const {
    t
  } = useTranslation();
  const onClose = () => {
    setCreatorOnboardingStep(CreatorOnboardingStep.Guideline);
  };
  return <ModalLayout onClose={onClose} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} isDim closeWhenClickOutside={false} data-sentry-element="ModalLayout" data-sentry-component="CreatorWelcomeModal" data-sentry-source-file="WelcomeModal.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="WelcomeModal.tsx">
        <WelcomeImage src='/images/match/creator/icCreator@4x.png' alt='Welcome' data-sentry-element="WelcomeImage" data-sentry-source-file="WelcomeModal.tsx" />
        <WelcomeHeader data-sentry-element="WelcomeHeader" data-sentry-source-file="WelcomeModal.tsx">
          <WelcomeTitle data-sentry-element="WelcomeTitle" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_benefit_title')}</WelcomeTitle>
          <WelcomeDescription data-sentry-element="WelcomeDescription" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_benefit_desc')}</WelcomeDescription>
        </WelcomeHeader>
        <CreatorBenefitsSection data-sentry-element="CreatorBenefitsSection" data-sentry-source-file="WelcomeModal.tsx">
          <CreatorBenefitsBox data-sentry-element="CreatorBenefitsBox" data-sentry-source-file="WelcomeModal.tsx">
            <BenefitsTitle data-sentry-element="BenefitsTitle" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_benefit_unlimited_filter_title')}</BenefitsTitle>
            <BenefitsContent data-sentry-element="BenefitsContent" data-sentry-source-file="WelcomeModal.tsx">
              <BenefitItem data-sentry-element="BenefitItem" data-sentry-source-file="WelcomeModal.tsx">
                <BenefitItemImage src='/images/item/imgItemAzarGuarantee.png' alt='Country Filter Item' data-sentry-element="BenefitItemImage" data-sentry-source-file="WelcomeModal.tsx" />
                <BenefitItemDescription data-sentry-element="BenefitItemDescription" data-sentry-source-file="WelcomeModal.tsx">
                  {t('tutorial_benefit_country_filter')}
                </BenefitItemDescription>
              </BenefitItem>
              <BenefitItem data-sentry-element="BenefitItem" data-sentry-source-file="WelcomeModal.tsx">
                <BenefitItemImage src='/images/item/imgGenderFilter.png' alt='Gender Filter Item' data-sentry-element="BenefitItemImage" data-sentry-source-file="WelcomeModal.tsx" />
                <BenefitItemDescription data-sentry-element="BenefitItemDescription" data-sentry-source-file="WelcomeModal.tsx">
                  {t('tutorial_benefit_gender_filter')}
                </BenefitItemDescription>
              </BenefitItem>
            </BenefitsContent>
          </CreatorBenefitsBox>
          <CreatorBenefitsBox data-sentry-element="CreatorBenefitsBox" data-sentry-source-file="WelcomeModal.tsx">
            <BenefitsTitle data-sentry-element="BenefitsTitle" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_excl_title')}</BenefitsTitle>
            <BenefitsContent data-sentry-element="BenefitsContent" data-sentry-source-file="WelcomeModal.tsx">
              <BenefitItem data-sentry-element="BenefitItem" data-sentry-source-file="WelcomeModal.tsx">
                <IcFaceFocusing data-sentry-element="IcFaceFocusing" data-sentry-source-file="WelcomeModal.tsx" />
                <BenefitItemDescription data-sentry-element="BenefitItemDescription" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_excl_face_filter')}</BenefitItemDescription>
              </BenefitItem>
              <BenefitItem data-sentry-element="BenefitItem" data-sentry-source-file="WelcomeModal.tsx">
                <IcHd data-sentry-element="IcHd" data-sentry-source-file="WelcomeModal.tsx" />
                <BenefitItemDescription data-sentry-element="BenefitItemDescription" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_excl_quality_filter')}</BenefitItemDescription>
              </BenefitItem>
              <BenefitItem data-sentry-element="BenefitItem" data-sentry-source-file="WelcomeModal.tsx">
                <IcLanguage data-sentry-element="IcLanguage" data-sentry-source-file="WelcomeModal.tsx" />
                <BenefitItemDescription data-sentry-element="BenefitItemDescription" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_excl_lang_filter')}</BenefitItemDescription>
              </BenefitItem>
              <BenefitItem data-sentry-element="BenefitItem" data-sentry-source-file="WelcomeModal.tsx">
                <IcMobileDesktopViewFill data-sentry-element="IcMobileDesktopViewFill" data-sentry-source-file="WelcomeModal.tsx" />
                <BenefitItemDescription data-sentry-element="BenefitItemDescription" data-sentry-source-file="WelcomeModal.tsx">{t('tutorial_excl_view_mode')}</BenefitItemDescription>
              </BenefitItem>
            </BenefitsContent>
          </CreatorBenefitsBox>
        </CreatorBenefitsSection>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="WelcomeModal.tsx">
        <FooterButton id='creator_onboarding_welcome_modal_footer_button' onClick={onClose} data-sentry-element="FooterButton" data-sentry-source-file="WelcomeModal.tsx">
          {t('tutorial_cta_btn')}
        </FooterButton>
      </ModalFooter>
    </ModalLayout>;
};
export default CreatorWelcomeModal;